import { useState, useEffect, useCallback } from 'react'
import {
  Wallet,
  Search,
  Filter,
  Loader2,
  AlertCircle,
  Plus,
  Minus,
  Snowflake,
  Flame,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  X,
  AlertTriangle,
  CheckCircle2,
  Euro,
  User,
  Calendar,
  ArrowUpRight,
  ArrowDownLeft,
  RotateCcw,
  TrendingUp,
  Ban,
  ShieldAlert,
} from 'lucide-react'
import { toast } from 'react-hot-toast'
import walletService, {
  type Wallet as WalletType,
  type WalletTransaction,
  type FraudFlag,
} from '../services/walletService'

type Tab = 'wallets' | 'transactions' | 'fraud'

const ITEMS_PER_PAGE = 15

const WalletManagementPage = () => {
  const [activeTab, setActiveTab] = useState<Tab>('wallets')

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Gestion des Portefeuilles</h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">
          Gérez les portefeuilles utilisateurs, consultez les transactions et surveillez les activités suspectes
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 p-1 rounded-lg w-fit mb-6">
        {[
          { id: 'wallets' as Tab, label: 'Portefeuilles', icon: Wallet },
          { id: 'transactions' as Tab, label: 'Transactions', icon: RefreshCw },
          { id: 'fraud' as Tab, label: 'Fraude', icon: ShieldAlert },
        ].map((tab) => {
          const Icon = tab.icon
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          )
        })}
      </div>

      {activeTab === 'wallets' && <WalletsTab />}
      {activeTab === 'transactions' && <TransactionsTab />}
      {activeTab === 'fraud' && <FraudTab />}
    </div>
  )
}

// ==================== Wallets Tab ====================
const WalletsTab = () => {
  const [wallets, setWallets] = useState<WalletType[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(0)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [showAdjustModal, setShowAdjustModal] = useState(false)
  const [adjustWallet, setAdjustWallet] = useState<WalletType | null>(null)
  const [adjustAmount, setAdjustAmount] = useState('')
  const [adjustReason, setAdjustReason] = useState('')
  const [adjusting, setAdjusting] = useState(false)

  const loadWallets = useCallback(async () => {
    setLoading(true)
    try {
      const res = await walletService.getWallets({
        search: search || undefined,
        status: statusFilter || undefined,
        limit: ITEMS_PER_PAGE,
        offset: page * ITEMS_PER_PAGE,
      })
      setWallets(res.data)
      setTotal(res.total)
    } catch {
      toast.error('Erreur lors du chargement des portefeuilles')
    } finally {
      setLoading(false)
    }
  }, [search, statusFilter, page])

  useEffect(() => { loadWallets() }, [loadWallets])

  const totalPages = Math.ceil(total / ITEMS_PER_PAGE)

  const handleFreeze = async (wallet: WalletType) => {
    try {
      if (wallet.status === 'frozen') {
        await walletService.unfreezeWallet(wallet.id)
        toast.success('Portefeuille dégelé')
      } else {
        await walletService.freezeWallet(wallet.id)
        toast.success('Portefeuille gelé')
      }
      loadWallets()
    } catch {
      toast.error('Erreur lors de la mise à jour')
    }
  }

  const openAdjust = (wallet: WalletType) => {
    setAdjustWallet(wallet)
    setAdjustAmount('')
    setAdjustReason('')
    setShowAdjustModal(true)
  }

  const handleAdjust = async () => {
    if (!adjustWallet || !adjustAmount || !adjustReason.trim()) {
      toast.error('Veuillez remplir tous les champs')
      return
    }
    const amount = parseFloat(adjustAmount)
    if (isNaN(amount) || amount === 0) {
      toast.error('Montant invalide')
      return
    }

    setAdjusting(true)
    try {
      await walletService.creditAdjustment({
        user_id: adjustWallet.user_id,
        amount,
        reason: adjustReason,
      })
      toast.success(`Ajustement de ${amount}€ effectué`)
      setShowAdjustModal(false)
      loadWallets()
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Erreur lors de l\'ajustement')
    } finally {
      setAdjusting(false)
    }
  }

  return (
    <div>
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0) }}
            placeholder="Rechercher par email ou nom..."
            className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(0) }}
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100"
        >
          <option value="">Tous les statuts</option>
          <option value="active">Actif</option>
          <option value="frozen">Gelé</option>
          <option value="closed">Fermé</option>
        </select>
      </div>

      {/* Wallet List */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary-600" /></div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Utilisateur</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Solde</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Dernière transaction</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {wallets.map((wallet) => (
                  <tr key={wallet.id} className="hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                          <User className="w-4 h-4 text-gray-500" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">{wallet.user_name || 'N/D'}</p>
                          <p className="text-xs text-gray-500">{wallet.user_email || ''}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm font-bold text-gray-900 dark:text-white">
                        {wallet.balance.toFixed(2)} {wallet.currency}
                      </p>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        wallet.status === 'active' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' :
                        wallet.status === 'frozen' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400' :
                        'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                      }`}>
                        {wallet.status === 'active' ? 'Actif' : wallet.status === 'frozen' ? 'Gelé' : 'Fermé'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-gray-500">
                        {wallet.last_transaction_at
                          ? new Date(wallet.last_transaction_at).toLocaleDateString()
                          : 'Aucune'}
                      </p>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => openAdjust(wallet)}
                          className="p-1.5 text-gray-400 hover:text-primary-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                          title="Ajuster le solde"
                        >
                          <Euro className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleFreeze(wallet)}
                          className="p-1.5 text-gray-400 hover:text-blue-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                          title={wallet.status === 'frozen' ? 'Dégeler' : 'Geler'}
                        >
                          {wallet.status === 'frozen' ? <Flame className="w-4 h-4" /> : <Snowflake className="w-4 h-4" />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {wallets.length === 0 && (
            <div className="p-12 text-center text-gray-400">
              <Wallet className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Aucun portefeuille trouvé</p>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <button onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0}
                className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 disabled:opacity-30">
                <ChevronLeft className="w-4 h-4" /> Précédent
              </button>
              <span className="text-sm text-gray-500">Page {page + 1} sur {totalPages}</span>
              <button onClick={() => setPage(Math.min(totalPages - 1, page + 1))} disabled={page >= totalPages - 1}
                className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 disabled:opacity-30">
                Suivant <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Adjust Modal */}
      {showAdjustModal && adjustWallet && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setShowAdjustModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">Ajustement de solde</h3>
              <button onClick={() => setShowAdjustModal(false)} className="p-1 text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <p className="text-sm text-gray-500">Utilisateur: <span className="font-medium text-gray-900 dark:text-white">{adjustWallet.user_name || adjustWallet.user_email}</span></p>
              <p className="text-sm text-gray-500">Solde actuel: <span className="font-bold text-gray-900 dark:text-white">{adjustWallet.balance.toFixed(2)} {adjustWallet.currency}</span></p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Montant</label>
                <div className="relative">
                  <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="number"
                    value={adjustAmount}
                    onChange={(e) => setAdjustAmount(e.target.value)}
                    placeholder="Montant (positif = crédit, négatif = débit)"
                    step="0.01"
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>
                <p className="text-xs text-gray-400 mt-1">Utilisez un nombre positif pour créditer, négatif pour débiter</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Raison</label>
                <textarea
                  value={adjustReason}
                  onChange={(e) => setAdjustReason(e.target.value)}
                  placeholder="Motif de l'ajustement..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button onClick={() => setShowAdjustModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-sm font-medium">
                Annuler
              </button>
              <button onClick={handleAdjust} disabled={adjusting}
                className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 text-sm font-medium flex items-center justify-center gap-2">
                {adjusting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                Confirmer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ==================== Transactions Tab ====================
const TransactionsTab = () => {
  const [transactions, setTransactions] = useState<WalletTransaction[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(0)
  const [typeFilter, setTypeFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [searchUser, setSearchUser] = useState('')

  const loadTransactions = useCallback(async () => {
    setLoading(true)
    try {
      const res = await walletService.getTransactions({
        type: typeFilter || undefined,
        status: statusFilter || undefined,
        limit: ITEMS_PER_PAGE,
        offset: page * ITEMS_PER_PAGE,
      })
      setTransactions(res.data)
      setTotal(res.total)
    } catch {
      toast.error('Erreur lors du chargement des transactions')
    } finally {
      setLoading(false)
    }
  }, [typeFilter, statusFilter, page])

  useEffect(() => { loadTransactions() }, [loadTransactions])

  const totalPages = Math.ceil(total / ITEMS_PER_PAGE)

  const typeConfig: Record<string, { label: string; icon: any; color: string }> = {
    top_up: { label: 'Recharge', icon: ArrowDownLeft, color: 'text-green-600' },
    payment: { label: 'Paiement', icon: ArrowUpRight, color: 'text-red-600' },
    refund: { label: 'Remboursement', icon: RotateCcw, color: 'text-blue-600' },
    credit_adjustment: { label: 'Crédit manuel', icon: Plus, color: 'text-purple-600' },
    debit_adjustment: { label: 'Débit manuel', icon: Minus, color: 'text-orange-600' },
  }

  return (
    <div>
      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6">
        <select value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setPage(0) }}
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-gray-100">
          <option value="">Tous les types</option>
          <option value="top_up">Recharge</option>
          <option value="payment">Paiement</option>
          <option value="refund">Remboursement</option>
          <option value="credit_adjustment">Crédit manuel</option>
          <option value="debit_adjustment">Débit manuel</option>
        </select>
        <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(0) }}
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-gray-100">
          <option value="">Tous les statuts</option>
          <option value="completed">Complété</option>
          <option value="pending">En cours</option>
          <option value="failed">Échoué</option>
          <option value="cancelled">Annulé</option>
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary-600" /></div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Description</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Montant</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Statut</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {transactions.map((tx) => {
                  const config = typeConfig[tx.type] || { label: tx.type, icon: RefreshCw, color: 'text-gray-600' }
                  const Icon = config.icon
                  const isCredit = tx.type === 'top_up' || tx.type === 'refund' || tx.type === 'credit_adjustment'
                  return (
                    <tr key={tx.id} className="hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Icon className={`w-4 h-4 ${config.color}`} />
                          <span className="text-sm text-gray-900 dark:text-white">{config.label}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-gray-900 dark:text-white">{tx.description}</p>
                        {tx.reference_id && (
                          <p className="text-xs text-gray-400">Ref: {tx.reference_id}</p>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <p className={`text-sm font-bold ${isCredit ? 'text-green-600' : 'text-red-600'}`}>
                          {isCredit ? '+' : '-'}{Math.abs(tx.amount).toFixed(2)} {tx.currency}
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          tx.status === 'completed' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' :
                          tx.status === 'pending' ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400' :
                          tx.status === 'failed' ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400' :
                          'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                        }`}>
                          {tx.status === 'completed' ? 'Complété' : tx.status === 'pending' ? 'En cours' : tx.status === 'failed' ? 'Échoué' : 'Annulé'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-gray-500">{new Date(tx.created_at).toLocaleDateString()}</p>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          {transactions.length === 0 && (
            <div className="p-12 text-center text-gray-400">
              <RefreshCw className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Aucune transaction trouvée</p>
            </div>
          )}

          {totalPages > 1 && (
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <button onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0}
                className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 disabled:opacity-30">
                <ChevronLeft className="w-4 h-4" /> Précédent
              </button>
              <span className="text-sm text-gray-500">Page {page + 1} sur {totalPages}</span>
              <button onClick={() => setPage(Math.min(totalPages - 1, page + 1))} disabled={page >= totalPages - 1}
                className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 disabled:opacity-30">
                Suivant <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ==================== Fraud Tab ====================
const FraudTab = () => {
  const [flags, setFlags] = useState<FraudFlag[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [showResolved, setShowResolved] = useState(false)

  const loadFlags = useCallback(async () => {
    setLoading(true)
    try {
      const res = await walletService.getFraudFlags({
        resolved: showResolved,
        limit: 50,
      })
      setFlags(res.data)
      setTotal(res.total)
    } catch {
      toast.error('Erreur lors du chargement des alertes')
    } finally {
      setLoading(false)
    }
  }, [showResolved])

  useEffect(() => { loadFlags() }, [loadFlags])

  const handleResolve = async (id: string) => {
    try {
      await walletService.resolveFraudFlag(id)
      toast.success('Alerte résolue')
      loadFlags()
    } catch {
      toast.error('Erreur lors de la résolution')
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-gray-500">{total} alerte{total > 1 ? 's' : ''}</p>
        <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
          <input type="checkbox" checked={showResolved} onChange={(e) => setShowResolved(e.target.checked)}
            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
          Afficher les résolues
        </label>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary-600" /></div>
      ) : (
        <div className="space-y-3">
          {flags.map((flag) => (
            <div key={flag.id} className={`bg-white dark:bg-gray-800 rounded-xl border p-5 transition-all ${
              flag.resolved ? 'border-gray-200 dark:border-gray-700 opacity-60' :
              flag.severity === 'high' ? 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/10' :
              flag.severity === 'medium' ? 'border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/10' :
              'border-gray-200 dark:border-gray-700'
            }`}>
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    flag.severity === 'high' ? 'bg-red-100 dark:bg-red-900/30' :
                    flag.severity === 'medium' ? 'bg-amber-100 dark:bg-amber-900/30' :
                    'bg-gray-100 dark:bg-gray-700'
                  }`}>
                    <AlertTriangle className={`w-5 h-5 ${
                      flag.severity === 'high' ? 'text-red-600' :
                      flag.severity === 'medium' ? 'text-amber-600' :
                      'text-gray-500'
                    }`} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium text-gray-900 dark:text-white">{flag.flag_type}</h4>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                        flag.severity === 'high' ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400' :
                        flag.severity === 'medium' ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400' :
                        'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                      }`}>
                        {flag.severity === 'high' ? 'Haute' : flag.severity === 'medium' ? 'Moyenne' : 'Basse'}
                      </span>
                      {flag.resolved && (
                        <span className="px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-[10px] font-medium rounded-full">
                          Résolue
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{flag.description}</p>
                    <p className="text-xs text-gray-400 mt-1">
                      {flag.user_name && `Utilisateur: ${flag.user_name} · `}
                      {new Date(flag.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                {!flag.resolved && (
                  <button onClick={() => handleResolve(flag.id)}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 dark:bg-green-900/20 dark:text-green-400 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    Résoudre
                  </button>
                )}
              </div>
            </div>
          ))}

          {flags.length === 0 && (
            <div className="p-12 text-center text-gray-400">
              <ShieldAlert className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Aucune alerte de fraude</p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default WalletManagementPage
